import React from 'react';
import Typography from '@mui/material/Typography';

import { color, r } from '../../theme/tokens';

// SafetyNote — the interaction-status line under the stack list.
//
// Shape ruled by Tanu, 2026-08-29 (STACK-528). Two rules drive the component:
//
//   1. Never claim a check that did not run. A failed check and a clean
//      check must not share a line — the clean path says nothing at all,
//      because we cannot verify the absence of an interaction.
//   2. Provider advice lives on StackDisclaimer, not here. The older
//      "Consult your healthcare provider…" sentence was removed so those
//      two lines cannot drift apart.
//
// Membership failure is a membership state, not a fault — same muted ink as
// every other line, never error colour. The lapsed backend string
// (`Active subscription required` from pro_feature.py) is mapped here, in
// the frontend only. Do not change the backend; that string is an API contract.

export const LAPSED_BACKEND_ERROR = 'Active subscription required';
export const MEMBERSHIP_LINE = 'Active membership is required for interaction checks.';
export const GENERIC_FAILURE_LINE = "We couldn't check for interactions right now.";

export const isMembershipCheckError = (error) =>
  typeof error === 'string' && error.includes(LAPSED_BACKEND_ERROR);

/**
 * Picks the conditional interaction line, or null when there is nothing
 * truthful to say.
 *
 * Returns null while the check is still running, and null when a completed
 * check found nothing: at those moments no claim about interactions is one
 * we can stand behind. A failed check is a different state from a clean
 * one — empty flags from an error must not fall through to the clean path.
 */
export const interactionLine = ({
  isLoggedIn,
  hasPrescriptions,
  hasFlags,
  checking = false,
  checkError = null,
}) => {
  if (!isLoggedIn) return 'Log in and add your prescriptions to check for interactions.';
  if (!hasPrescriptions) return 'Add your prescriptions to check for interactions.';
  if (checking) return null;
  if (checkError) {
    return isMembershipCheckError(checkError) ? MEMBERSHIP_LINE : GENERIC_FAILURE_LINE;
  }
  if (hasFlags) return 'Interaction flags are screened against your prescriptions.';
  return null;
};

export const GUEST_FLAGS_LINE = 'Interaction flags are screened against the medications you added.';

/**
 * The line for a logged-out visitor who added medications (STACK-565, mock
 * s9a–s11). They attached medications rather than entering prescriptions, so
 * the flags line names them that way. A refusal gets the generic failure
 * line. Clean and checking say nothing here: the "Your medications" panel's
 * status line already does, and two lines would disagree as one updates.
 *
 * @param {string} guestStatus - useGuestInteractionCheck's status
 */
export const guestInteractionLine = (guestStatus) => {
  if (guestStatus === 'flags') return GUEST_FLAGS_LINE;
  if (guestStatus === 'couldnt_check') return GENERIC_FAILURE_LINE;
  return null;
};

const SafetyNote = ({ isLoggedIn, hasPrescriptions, hasFlags, checking, checkError, guestStatus = null }) => {
  // `guestStatus` is set only for a logged-out visitor who added medications;
  // everyone else keeps `interactionLine` exactly as it was.
  const line = guestStatus
    ? guestInteractionLine(guestStatus)
    : interactionLine({ isLoggedIn, hasPrescriptions, hasFlags, checking, checkError });
  if (!line) return null;

  return (
    <Typography
      data-testid="safety-note"
      sx={{
        mt: r(16),
        fontSize: r(11.5),
        color: color.inkMuted,
        lineHeight: 1.55,
      }}
    >
      {line}
    </Typography>
  );
};

export default SafetyNote;
